import { useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { logActivity } from "@/lib/logActivity";

type Contract = {
  id: string;
  tenant_id: string;
  title: string;
  rendered_content: string | null;
  signature_status: string | null;
  status: string;
};

interface Props {
  contract: Contract;
  tenantId: string;
  onUpdated: (updated: Partial<Contract>) => void;
}

export default function TextTab({ contract, tenantId, onUpdated }: Props) {
  const isSigned = contract.signature_status === "signed";
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(contract.rendered_content ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("contracts")
      .update({ rendered_content: content, updated_at: new Date().toISOString() })
      .eq("id", contract.id)
      .eq("tenant_id", tenantId);
    if (!error) {
      onUpdated({ rendered_content: content });
      logActivity({
        tenantId,
        action: "updated",
        entity: "contract",
        entityId: contract.id,
        entityName: contract.title,
      });
      setEditing(false);
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setContent(contract.rendered_content ?? "");
    setEditing(false);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center bg-[#C8A96E]/10 rounded-lg">
            <i className="ri-file-text-line text-[#C8A96E] text-sm"></i>
          </div>
          <div>
            <p className="text-[#0f172a] text-sm font-medium">{contract.title}</p>
            <p className="text-slate-400 text-xs">Contract text</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isSigned && (
            <span className="flex items-center gap-1.5 text-emerald-600 text-xs bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg">
              <i className="ri-lock-line"></i>
              Locked — contract is signed
            </span>
          )}
          {!isSigned && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0f172a] bg-white hover:bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-pencil-line"></i>
              Edit Text
            </button>
          )}
          {editing && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 text-sm bg-[#C8A96E] hover:bg-[#b8965a] text-white font-semibold px-4 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Save
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content area */}
      {editing ? (
        <div className="bg-white rounded-xl border border-[#C8A96E]/30 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50">
            <i className="ri-edit-2-line text-[#C8A96E] text-xs"></i>
            <span className="text-slate-400 text-xs">Editing contract text — HTML supported</span>
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full bg-white text-[#0f172a] text-sm p-5 outline-none resize-none font-mono leading-relaxed"
            style={{ minHeight: "520px" }}
            placeholder="Enter contract text here. HTML is supported for formatting."
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {contract.rendered_content ? (
            <div className="p-6 lg:p-8">
              <div
                className="contract-text prose max-w-none text-[#0f172a] text-sm leading-relaxed"
                style={{
                  fontFamily: "'Georgia', serif",
                  lineHeight: "1.8",
                }}
                dangerouslySetInnerHTML={{ __html: contract.rendered_content }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100">
                <i className="ri-file-text-line text-slate-300 text-2xl"></i>
              </div>
              <div className="text-center">
                <p className="text-slate-600 text-sm font-medium mb-1">No contract text yet</p>
                <p className="text-slate-400 text-xs">
                  {isSigned
                    ? "This contract was signed without text content."
                    : "Click \"Edit Text\" to add the contract body."}
                </p>
              </div>
              {!isSigned && (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-2 text-sm bg-[#C8A96E] hover:bg-[#b8965a] text-white font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-pencil-line"></i>
                  Add Contract Text
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Signed watermark notice */}
      {isSigned && contract.rendered_content && (
        <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
          <i className="ri-shield-check-line"></i>
          This contract has been digitally signed and cannot be modified.
        </div>
      )}
    </div>
  );
}